import clsx from 'clsx'
import {
  pressCategory,
  writerCategoryRows,
  type WriterCategoryRows,
} from 'lib/community/discussions-writer'
import type { CommunityCategoryNode } from 'lib/community/types'
import base from 'components/community/community.module.css'
import styles from './Writer.module.css'

interface CategoryPanelProps {
  /** DealHunt's full category list: the top-level pills, and each one's children. */
  categories: CommunityCategoryNode[]
  /** The node picked (any depth); sent as `category_node_id`. */
  selected: string | null
  onSelect: (id: string | null) => void
}

const Pills: React.VFC<{
  nodes: CommunityCategoryNode[]
  lit: string | null
  level: 'top' | 'child'
  rows: WriterCategoryRows
  label: string
  onSelect: (id: string | null) => void
}> = ({ nodes, lit, level, rows, label, onSelect }) => (
  <div className={clsx(styles.pills, { [styles.pillsSub]: level === 'child' })} aria-label={label}>
    {nodes.map(node => {
      const on = node.id === lit
      return (
        <button
          key={node.id}
          type="button"
          className={clsx(styles.pill, { [styles.pillOn]: on })}
          aria-pressed={on}
          onClick={() => onSelect(pressCategory(rows, node, level))}
        >
          {node.name}
        </button>
      )
    })}
  </div>
)

/**
 * CATEGORY: single-select. The top-level pills; once one is picked, its children as a second row,
 * and a child picks the child node. Pressing the lit child goes back to its parent; pressing the
 * lit parent clears the category (`pressCategory`).
 */
const CategoryPanel: React.VFC<CategoryPanelProps> = ({ categories, selected, onSelect }) => {
  const rows = writerCategoryRows(categories, selected)
  return (
    <section className={base.card} aria-labelledby="writer-category">
      <h2 id="writer-category" className={base.eyebrow}>
        {'Category'}
      </h2>
      <Pills
        nodes={rows.top}
        lit={rows.parentId}
        level="top"
        rows={rows}
        label="Categories"
        onSelect={onSelect}
      />
      {rows.children.length > 0 && (
        <Pills
          nodes={rows.children}
          lit={rows.childId}
          level="child"
          rows={rows}
          label="Subcategories"
          onSelect={onSelect}
        />
      )}
    </section>
  )
}

export default CategoryPanel
